/* eslint-disable */

import { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Button,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Chip,
  ListItemText,
  Checkbox,
  Divider,
  List,
  ListItem,
  IconButton,
  Collapse,
  Tabs,
  Tab,
  Grid,
  Paper,
  FormGroup,
} from "@mui/material";
import { fetcher, patchFetcher, postFetcher } from "src/utils/axios";
import { enqueueSnackbar } from "notistack";
import Iconify from "src/components/iconify";
import OutputDeleteConfirmationDialog from "./OutputDeleteConfirmationDialog ";
import DeleteConfirmationInsideEdit from "./DeleteConfirmationInsideEdit";

// ─── Shared style tokens (matching screenshot color scheme) ───────────────────
const colors = {
  bg: "#F2F2F2",           // page / dialog background
  surface: "#FFFFFF",       // card / section surface
  border: "#E0E0E0",        // subtle borders
  borderDark: "#C8C8C8",    // stronger borders
  text: "#1A1A1A",          // primary text
  textSecondary: "#6B6B6B", // secondary / label text
  accent: "#1A1A1A",        // black CTA (matches bold button in screenshot)
  accentHover: "#333333",
  sidebarBg: "#F7F7F7",
  sidebarSelected: "#1A1A1A",
  sidebarSelectedText: "#FFFFFF",
  chipBg: "#EDEDED",
  chipBorder: "#D0D0D0",
  chipPrimary: "#1A1A1A",
  chipPrimaryText: "#FFFFFF",
  error: "#D32F2F",
  tabSelected: "#1A1A1A",
  tabBorder: "#1A1A1A",
};

const sectionStyle = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: "10px",
  p: 2.5,
  mb: 2,
};

const labelStyle = {
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.08em",
  color: colors.textSecondary,
  textTransform: "uppercase",
  mb: 1,
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const fieldSx = {
  mb: 2,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    background: colors.surface,
    fontSize: "14px",
    "& fieldset": { borderColor: colors.border },
    "&:hover fieldset": { borderColor: colors.borderDark },
    "&.Mui-focused fieldset": { borderColor: colors.accent, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "13px",
    color: colors.textSecondary,
    "&.Mui-focused": { color: colors.accent },
  },
};

// ─── AddUnitDialog ─────────────────────────────────────────────────────────────
const AddUnitDialog = ({ open, onClose, onRefresh, onSave, existingUnits }) => {
  const [unitAbbreviation, setUnitAbbreviation] = useState("");

  const handleSave = async () => {
    if (existingUnits.includes(unitAbbreviation.trim())) {
      alert("This unit already exists");
      return;
    }
    const payload = { name: unitAbbreviation.trim() };
    const res = await postFetcher(["/new/workflow-app/measurement/unit/create/", payload]);
    if (onRefresh) onRefresh();
    onSave(unitAbbreviation.trim());
    setUnitAbbreviation("");
    onClose();
  };

  const handleClose = () => {
    setUnitAbbreviation("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          background: colors.surface,
          boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "16px",
          fontWeight: 700,
          color: colors.text,
          borderBottom: `1px solid ${colors.border}`,
          pb: 1.5,
        }}
      >
        Add New Unit
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <TextField
          label="Unit Abbreviation"
          value={unitAbbreviation}
          onChange={(e) => setUnitAbbreviation(e.target.value)}
          fullWidth
          margin="normal"
          placeholder="e.g., kg"
          sx={fieldSx}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: "8px",
            borderColor: colors.border,
            color: colors.textSecondary,
            fontWeight: 600,
            fontSize: "13px",
            "&:hover": { borderColor: colors.borderDark, background: colors.bg },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!unitAbbreviation.trim()}
          sx={{
            borderRadius: "8px",
            background: colors.accent,
            color: "#fff",
            fontWeight: 700,
            fontSize: "13px",
            boxShadow: "none",
            "&:hover": { background: colors.accentHover, boxShadow: "none" },
          }}
        >
          Add Unit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── AddOutputDialog ────────────────────────────────────────────────────────────
const AddOutputDialog = ({
  open,
  onClose,
  onSave,
  rawMaterialInputs,
  editRawMaterialInputs,
  previousNodeInputs,
  showOutputDialog,
  allocatedoutputs = [],
  finalproduct,
  nodeType,
  editingOutput,
  setupdatedoutputids,
  allOutputIds,
  setAllOutputIds,
  engineRef,
  setAllocatedoutputs,
  handleNodeSelection,
  editPreviousNodeInputs,
  setNodes,
  setAvailableNodes,
  onUnitsSelected,
  outputids,
  predefinedoutput,
  predefined,
  setPredefined,
  directLinks = [],
  downstreamLinks = [],
  directNodes = [],
  downstreamNodes = [],
  setDirectLinks,
  setDownstreamLinks,
  setDirectNodes,
  setDownstreamNodes,
  selectedLinks = {},
  setSelectedLinks,
  selectedNodes = {},
  setSelectedNodes,
  showdeleteeditDialog,
  setdeleteeditDialog,
  onDeleteOutput,
  rfNodes = [],
  setRfNodes,
  rfEdges = [],
  setRfEdges,
  onRefresh,
}) => {
  const [numberOfOutputs, setNumberOfOutputs] = useState(1);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [outputs, setOutputs] = useState([
    { name: "", outputName: "", unit: "", description: "", quantity: 0, isConnected: "no", connectedOutput: "", inputForNextProcess: "no", question: "", measurement_unit: "" },
  ]);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [showAddUnitDialog, setShowAddUnitDialog] = useState(false);
  const [attributesList, setAttributesList] = useState([]);
  const [nodeOutputs, setNodeOutputs] = useState([]);
  const [selectedNodeOutput, setSelectedNodeOutput] = useState({});
  const [availableOutputs, setAvailableOutputs] = useState([]);
  const [lastPostedIndex, setLastPostedIndex] = useState(-1);
  const [permanentdeleteeditnode, setpermanentdeleteeditnode] = useState(false);
  const [savedOutputIds, setSavedOutputIds] = useState(() => {
    const saved = sessionStorage.getItem("savedOutputIds");
    return saved ? JSON.parse(saved) : [];
  });
  const [connectedOutputAttributes, setConnectedOutputAttributes] = useState({});
  const [linked_attributes, setLinkedAttributes] = useState([]);
  const [question, setquestion] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [outputToDelete, setOutputToDelete] = useState(null);
  const [availableMeasurementUnits, setAvailableMeasurementUnits] = useState([]);
  
  // New state variables for transformation section
  const [needsTransformation, setNeedsTransformation] = useState({});
  const [selectedTransformAttributes, setSelectedTransformAttributes] = useState({});
  const [transformOperators, setTransformOperators] = useState({});
  const [transformUserValues, setTransformUserValues] = useState({});

  const stableRawMaterialInputs = useMemo(() => rawMaterialInputs || [], [rawMaterialInputs]);
  const stableNodeInputs = useMemo(
    () => previousNodeInputs.length > 0 ? previousNodeInputs : editPreviousNodeInputs ? editPreviousNodeInputs : [],
    [previousNodeInputs, editPreviousNodeInputs]
  );

  // ── All handler logic preserved exactly from original ──────────────────────

  const handleLocalDelete = (index) => {
    const deletedOutput = outputs[index];
    const newOutputs = outputs.filter((_, i) => i !== index);
    setOutputs(newOutputs);
    setNumberOfOutputs(newOutputs.length);
    setLinkedAttributes((prev) => prev.filter((_, i) => i !== index));
    if (deletedOutput?.id) setAllOutputIds((prev) => prev.filter((id) => id !== deletedOutput.id));
    enqueueSnackbar(`Output ${index + 1} removed locally`, { variant: "info" });
  };

  const handleClosedelete = () => {
    try {
      if (setDirectLinks) setDirectLinks([]);
      if (setDownstreamLinks) setDownstreamLinks([]);
      if (setDirectNodes) setDirectNodes([]);
      if (setDownstreamNodes) setDownstreamNodes([]);
      if (setSelectedLinks) setSelectedLinks({});
      if (setSelectedNodes) setSelectedNodes({});
      setDeleteConfirmOpen(false);
      setOutputToDelete(null);
    } catch (error) {
      console.error("Error in handleClosedelete:", error);
    }
  };

  const handleDeleteOutput = (index) => {
    const output = outputs[index];
    if (!output?.id) { handleLocalDelete(index); return; }
    if (onDeleteOutput) onDeleteOutput(output.id);
    setOutputToDelete({ index, outputId: output.id });
    setDeleteConfirmOpen(true);
  };

const confirmDelete = async () => {
  const { index, outputId } = outputToDelete;
  try {
    const selectedLinkIds = Object.keys(selectedLinks).filter((id) => selectedLinks[id]);
    const selectedNodeIds = Object.keys(selectedNodes).filter((id) => selectedNodes[id]);
    
    console.log('Deleting nodes with IDs:', selectedNodeIds);
    console.log('Deleting edges with IDs:', selectedLinkIds);
    console.log('Current rfEdges before deletion:', rfEdges);
    
    const allLinks = [...directLinks, ...downstreamLinks];
    const incomingLinksToDeletedNodes = allLinks.filter((link) => selectedNodeIds.includes(link.target));
    const sourceNodeOutputIds = [];
    incomingLinksToDeletedNodes.forEach((link) => { if (link.outputId) sourceNodeOutputIds.push(link.outputId); });
    
    if (sourceNodeOutputIds.length > 0) {
      setAllocatedoutputs((prev) => prev.filter((id) => !sourceNodeOutputIds.includes(id)));
    }
    
    // Handle Edge Deletion (when only edges are selected, no nodes)
    if (selectedLinkIds.length > 0 && selectedNodeIds.length === 0) {
      console.log('Deleting only edges:', selectedLinkIds);
      
      // Remove selected edges from rfEdges
      if (rfEdges && setRfEdges) {
        setRfEdges((prevEdges) => {
          const filteredEdges = prevEdges.filter((edge) => !selectedLinkIds.includes(edge.id));
          console.log('rfEdges after edge deletion:', filteredEdges);
          return filteredEdges;
        });
      }
      
      // Collect output IDs from deleted edges to free them
      selectedLinkIds.forEach((linkId) => {
        const link = allLinks.find((l) => l.id === linkId);
        if (link?.outputId) {
          if (setAllocatedoutputs) {
            setAllocatedoutputs((prev) => prev.filter((id) => id !== link.outputId));
          }
        }
      });
      
      // Update the link state variables
      const remainingLinks = allLinks.filter((link) => !selectedLinkIds.includes(link.id));
      const remainingDirectLinks = remainingLinks.filter((link) => directLinks.some((dl) => dl.id === link.id));
      const remainingDownstreamLinks = remainingLinks.filter((link) => downstreamLinks.some((dl) => dl.id === link.id));
      
      if (setDirectLinks) setDirectLinks(remainingDirectLinks);
      if (setDownstreamLinks) setDownstreamLinks(remainingDownstreamLinks);
      
      // Update selected links state
      const remainingSelectedLinks = {};
      Object.keys(selectedLinks).forEach((linkId) => { 
        if (!selectedLinkIds.includes(linkId)) remainingSelectedLinks[linkId] = selectedLinks[linkId]; 
      });
      setSelectedLinks(remainingSelectedLinks);
      
      // Skip node deletion logic
      handleLocalDelete(index);
      enqueueSnackbar("Edge deleted successfully", { variant: "success" });
      setdeleteeditDialog(false);
      setDeleteConfirmOpen(false);
      setOutputToDelete(null);
      return;
    }
    
    // Handle Node Deletion (with or without edges)
    if (selectedNodeIds.length > 0) {
      // Remove ALL edges connected to deleted nodes from rfEdges
      if (rfEdges && setRfEdges) {
        setRfEdges((prevEdges) => {
          const filteredEdges = prevEdges.filter((edge) => {
            // Remove edge if source OR target is in selectedNodeIds
            const shouldKeep = !selectedNodeIds.includes(edge.source) && !selectedNodeIds.includes(edge.target);
            if (!shouldKeep) {
              console.log(`Removing edge ${edge.id} connected to deleted node`);
            }
            return shouldKeep;
          });
          console.log('rfEdges after filtering:', filteredEdges);
          return filteredEdges;
        });
      }
      
      // Remove nodes from rfNodes
      if (rfNodes && setRfNodes) {
        setRfNodes((prevNodes) => {
          const filteredNodes = prevNodes.filter((node) => !selectedNodeIds.includes(node.id));
          console.log('rfNodes after filtering:', filteredNodes);
          return filteredNodes;
        });
      }
      
      const collectedOutputIds = [];
      const remainingLinks = allLinks.filter((link) => !selectedLinkIds.includes(link.id));
      const remainingDirectLinks = remainingLinks.filter((link) => directLinks.some((dl) => dl.id === link.id));
      const remainingDownstreamLinks = remainingLinks.filter((link) => downstreamLinks.some((dl) => dl.id === link.id));
      
      if (setDirectLinks) setDirectLinks(remainingDirectLinks);
      if (setDownstreamLinks) setDownstreamLinks(remainingDownstreamLinks);
      
      const remainingSelectedLinks = {};
      Object.keys(selectedLinks).forEach((linkId) => { 
        if (!selectedLinkIds.includes(linkId)) remainingSelectedLinks[linkId] = selectedLinks[linkId]; 
      });
      setSelectedLinks(remainingSelectedLinks);
      
      selectedLinkIds.forEach((linkId) => {
        const link = allLinks.find((l) => l.id === linkId);
        if (link?.outputId) {
          collectedOutputIds.push(link.outputId);
          if (setAllocatedoutputs) setAllocatedoutputs((prev) => prev.filter((id) => id !== link.outputId));
        }
      });
      
      const allNodes = [...directNodes, ...downstreamNodes];
      const remainingNodes = allNodes.filter((node) => !selectedNodeIds.includes(node.id));
      const remainingDirectNodes = remainingNodes.filter((node) => directNodes.some((dn) => dn.id === node.id));
      const remainingDownstreamNodes = remainingNodes.filter((node) => downstreamNodes.some((dn) => dn.id === node.id));
      
      if (setDirectNodes) setDirectNodes(remainingDirectNodes);
      if (setDownstreamNodes) setDownstreamNodes(remainingDownstreamNodes);
      
      const remainingSelectedNodes = {};
      Object.keys(selectedNodes).forEach((nodeId) => { 
        if (!selectedNodeIds.includes(nodeId)) remainingSelectedNodes[nodeId] = selectedNodes[nodeId]; 
      });
      setSelectedNodes(remainingSelectedNodes);
      
      const backendIdsToDelete = [];
      const nodeNamesToDelete = [];
      selectedNodeIds.forEach((nodeId) => { 
        const node = allNodes.find((n) => n.id === nodeId); 
        if (node?.backendId) {
          backendIdsToDelete.push(node.backendId);
          nodeNamesToDelete.push(node.name);
        }
      });
      
      console.log("Nodes selected for deletion:", nodeNamesToDelete, "with backend IDs:", backendIdsToDelete);
      
      if (collectedOutputIds.length > 0) {
        try { 
          await postFetcher([`/new/workflow-app/delete/outputs/`, { ids: collectedOutputIds }]); 
        } catch (error) { 
          console.error("Error deleting outputs:", error); 
        }
      }
      
      if (backendIdsToDelete.length > 0) {
        console.log("Deleting nodes with backend IDs:", backendIdsToDelete);
        try {
          // Uncomment this if you want to delete from backend
          // await postFetcher([`/new/workflow-app/delete/node/`, { node_ids: backendIdsToDelete }]);
          
          // FIX: Clear all data except id and name from availableNodes for deleted nodes
          if (setAvailableNodes) {
            setAvailableNodes((prev) => {
              return prev.map(node => {
                // Check if this node's name or backendId is in the list of deleted nodes
                if (nodeNamesToDelete.includes(node.name) || backendIdsToDelete.includes(node.backendId)) {
                  console.log(`Clearing data for deleted node: ${node.name} (ID: ${node.id})`);
                  return {
                    backendId: node.backendId,
                    id: node.id,
                    name: node.name,
                    // Keep minimal display data
                    type: node.type || 'default',
                    color: node.color || '#00c0ff',
                    isPredefined: node.isPredefined || false,
                    // Clear all other data that could trigger auto-connections
                    input: [],
                    output: [],
                    cost: [],
                    machine: [],
                    attachments: [],
                    input_foreign: [],
                    output_foreign: [],
                    cost_foreign: [],
                    machine_data: [],
                    input_foreign_ids: [],
                    output_foreign_ids: [],
                    cost_foreign_ids: [],
                  };
                }
                return node;
              });
            });
          }
          
          if (setNodes) setNodes((prevNodes) => prevNodes.filter((node) => !backendIdsToDelete.includes(node.id)));
        } catch (error) { 
          enqueueSnackbar("Failed to delete nodes from backend", { variant: "error" }); 
        }
      }
    }
    
    handleLocalDelete(index);
    enqueueSnackbar("Output deleted successfully", { variant: "success" });
    setdeleteeditDialog(false);
  } catch (error) {
    enqueueSnackbar("Failed to delete output and dependencies", { variant: "error" });
  } finally {
    setDeleteConfirmOpen(false);
    setOutputToDelete(null);
  }
};

  useEffect(() => {
    sessionStorage.setItem("savedOutputIds", JSON.stringify(savedOutputIds));
  }, [savedOutputIds]);

  const getRawFormulaString = (formulaObj) => {
    if (!formulaObj) return "";
    if (formulaObj.formulaStr && typeof formulaObj.formulaStr === "string") return formulaObj.formulaStr.trim();
    if (!formulaObj.operation || !Array.isArray(formulaObj.operands)) return "";
    const opMap = { add: "+", subtract: "-", multiply: "*", divide: "/" };
    const operator = opMap[formulaObj.operation] || formulaObj.operation;
    const processOperand = (op) => {
      if (typeof op === "number") return op.toString();
      if (typeof op === "string") return op;
      if (op && typeof op === "object") return `(${getRawFormulaString(op)})`;
      return "";
    };
    return formulaObj.operands.map(processOperand).filter(Boolean).join(` ${operator} `);
  };

  useEffect(() => {
    if (!open) return;
    if (editingOutput) {
      if (Array.isArray(editingOutput)) {
        const loadedOutputs = editingOutput.map((output) => ({
          id: output.id,
          outputName: output.name || "",
          name: getRawFormulaString(output.formula) || "",
          unit: output.unit || "",
          description: output.description || "",
          quantity: output.value?.quantity || 0,
          isConnected: output.parent_output ? "yes" : "no",
          connectedOutput: "",
          inputForNextProcess: output.input_for_next_process ? "yes" : "no",
          question: output.question || "",
        }));
        setNumberOfOutputs(loadedOutputs.length);
        setOutputs(loadedOutputs);
        setAllOutputIds(editingOutput.map((o) => o.id));
        setquestion(editingOutput[0].question);
      } else {
        const rawFormula = getRawFormulaString(editingOutput.formula);
        setAllOutputIds([editingOutput.id]);
        setOutputs([{
          id: editingOutput.id,
          outputName: editingOutput.name || "",
          name: rawFormula,
          unit: editingOutput.unit || "",
          description: editingOutput.description || "",
          quantity: editingOutput.value?.quantity || 0,
          isConnected: editingOutput.parent_output ? "yes" : "no",
          connectedOutput: "",
          inputForNextProcess: editingOutput.input_for_next_process ? "yes" : "no",
          question: editingOutput.question || "",
        }]);
      }
    } else {
      setAllOutputIds([]);
      setNumberOfOutputs(1);
      setOutputs([{ name: "", outputName: "", unit: "", description: "", quantity: 0, isConnected: "no", connectedOutput: "", inputForNextProcess: "no", question: "" }]);
      setLinkedAttributes([]);
    }
  }, [open, editingOutput]);

  const getAllAvailableLinkedAttributes = useMemo(() => {
    const allAttributes = [];
    attributesList.forEach((material) => {
      material.attributes.forEach((attribute) => {
        allAttributes.push({ productId: material.id, attribute, displayName: `${material.name}: ${attribute}`, type: "material" });
      });
    });
    nodeOutputs.forEach((node) => {
      node.outputs.forEach((output) => {
        if (output.linked_attributes && output.linked_attributes.length > 0) {
          output.linked_attributes.forEach((attr) => {
            const product = attributesList.find((m) => m.id === attr.productId);
            const productName = product ? product.name : `Product ${attr.productId}`;
            allAttributes.push({ productId: attr.productId, attribute: attr.attribute, displayName: `${output.name} (${node.nodeName}): ${productName}.${attr.attribute}`, type: "node_output", nodeName: node.nodeName, outputName: output.name });
          });
        }
      });
    });
    return allAttributes;
  }, [attributesList, nodeOutputs]);

  useEffect(() => {
    if (open && getAllAvailableLinkedAttributes.length > 0) {
      const newLinkedAttributes = [];
      for (let i = 0; i < numberOfOutputs; i++) {
        if (i < linked_attributes.length) newLinkedAttributes[i] = linked_attributes[i];
        else newLinkedAttributes[i] = getAllAvailableLinkedAttributes.map((attr) => ({ productId: attr.productId, attribute: attr.attribute }));
      }
      setLinkedAttributes(newLinkedAttributes);
    }
  }, [numberOfOutputs, open, getAllAvailableLinkedAttributes]);

  useEffect(() => {
    if (open && getAllAvailableLinkedAttributes.length > 0) {
      const initialLinkedAttributes = outputs.map(() =>
        getAllAvailableLinkedAttributes.map((attr) => ({ productId: attr.productId, attribute: attr.attribute }))
      );
      setLinkedAttributes(initialLinkedAttributes);
    }
  }, [open, getAllAvailableLinkedAttributes, outputs.length]);

  useEffect(() => {
    if (open) {
      setAvailableOutputs([]);
      setSavedOutputIds([]);
      setSelectedNodeOutput({});
      setLinkedAttributes([]);
    }
  }, [open]);

  const filteredNodeOutputs = useMemo(() => {
    if (!nodeOutputs.length) return [];
    const isEditing = !!editPreviousNodeInputs;
    return nodeOutputs
      .map((node) => ({
        ...node,
        outputs: node.outputs.filter((output) => {
          if (isEditing) return true;
          return !allocatedoutputs.includes(output.id.toString());
        }),
      }))
      .filter((node) => node.outputs.length > 0);
  }, [nodeOutputs, allocatedoutputs, editPreviousNodeInputs]);

  useEffect(() => {
    if (!showOutputDialog) {
      setAvailableOutputs([]);
      setSavedOutputIds([]);
      setSelectedNodeOutput({});
      setLinkedAttributes([]);
    }
  }, [showOutputDialog]);

  const fetchNodeAttributes = async () => {
    try {
      if (!stableNodeInputs || stableNodeInputs.length === 0) { setNodeOutputs([]); return; }
      const nodeOutputsData = [];
      for (const node of stableNodeInputs) {
        if (node.backendId) {
          const res = await fetcher(`/new/workflow-app/node/${node.backendId}/`);
          if (res && res.output_foreign) {
            const outputs = res.output_foreign.map((output) => ({ id: output.id, name: output.name, attributes: output.attributes.map((attr) => attr.key), linked_attributes: output.linked_attributes || [], nodeName: node.label }));
            nodeOutputsData.push({ nodeId: node.backendId, nodeName: node.label, outputs });
          }
        }
      }
      setNodeOutputs(nodeOutputsData.length ? nodeOutputsData : []);
    } catch (error) {
      setNodeOutputs([]);
      enqueueSnackbar("Failed to fetch node outputs", { variant: "error" });
    }
  };

  const fetchUnits = async () => {
    try {
      const rawMaterialIds = stableRawMaterialInputs.map((material) => Number(material.value));
      const finalProductIds = finalproduct && Array.isArray(finalproduct) ? finalproduct.map((product) => Number(product.id)) : [];
      const productIds = [...rawMaterialIds, ...finalProductIds];
      const payload = { product_ids: productIds || [] };
      const res = await postFetcher(["/new/workflow-app/products/unit/", payload]);
      let allUnits = [];
      if (res) {
        if (Array.isArray(res.category_data)) res.category_data.forEach((category) => { if (Array.isArray(category.units)) allUnits.push(...category.units); });
        if (Array.isArray(res.units_data)) res.units_data.forEach((item) => { if (item && typeof item.name === "string") allUnits.push(item.name); });
        allUnits = [...new Set(allUnits.filter((unit) => unit && unit.trim() !== ""))];
        setAvailableUnits(allUnits);
      } else setAvailableUnits([]);
    } catch (error) {
      setAvailableUnits([]);
      enqueueSnackbar("Failed to fetch units", { variant: "error" });
    }
  };

  const fetchAttributes = async () => {
    try {
      const rawMaterialIds = stableRawMaterialInputs.map((material) => Number(material.value));
      const finalProductIds = finalproduct && Array.isArray(finalproduct) ? finalproduct.map((product) => Number(product.id)) : [];
      const productIds = [...rawMaterialIds, ...finalProductIds];
      if (!productIds.length) { setAttributesList([]); return; }
      const res = await postFetcher(["/new/workflow-app/products/attributes/key/", { product_ids: productIds }]);
      if (res) {
        const attributes = Array.isArray(res) ? res.map((item) => ({ name: item.name, attributes: item.attributes || [], id: item.id })) : res.data?.map((item) => ({ name: item.name, attributes: item.attributes || [], id: item.id })) || [];
        setAttributesList(attributes.length ? attributes : []);
      } else setAttributesList([]);
    } catch (error) {
      setAttributesList([]);
      enqueueSnackbar("Failed to fetch attributes", { variant: "error" });
    }
  };

  const fetchAvailableOutputs = async () => {
    try {
      if (savedOutputIds.length > 0) {
        const res = await postFetcher(["/new/workflow-app/output/list/", { output_ids: savedOutputIds }]);
        if (res) {
          const outputs = Array.isArray(res) ? res : res.data?.outputs || [];
          const mappedOutputs = outputs.map((output, index) => {
            const operands = output.formula?.operands || [];
            const attributes = operands.filter((op) => typeof op === "string" && op.includes(".")).map((op) => op.split(".")[1]) || [];
            return { name: output.output_name, index: output.output_id || index + 1, attributes };
          }).filter((output) => output.name?.trim());
          setAvailableOutputs(mappedOutputs.length ? mappedOutputs : []);
        } else setAvailableOutputs([]);
      }
    } catch (error) {
      setAvailableOutputs([]);
      enqueueSnackbar("Failed to fetch outputs", { variant: "error" });
    }
  };

  const fetchConnectedOutputAttributes = async (outputName, index) => {
    try {
      const output = availableOutputs.find((o) => o.name === outputName);
      if (!output) { setConnectedOutputAttributes((prev) => ({ ...prev, [index]: { id: null, attributes: [] } })); return; }
      setConnectedOutputAttributes((prev) => ({ ...prev, [index]: { id: output.index, attributes: output.attributes || [] } }));
    } catch (error) {
      setConnectedOutputAttributes((prev) => ({ ...prev, [index]: { id: null, attributes: [] } }));
    }
  };

  const handleUnitShow = async () => {
    try {
      const res = await fetcher([`/new/workflow-app/measurement/unit/show/`]);
      let allUnits = [];
      if (res) res.forEach((item) => allUnits.push(item));
      setAvailableMeasurementUnits(allUnits);
    } catch (error) {
      setAvailableUnits([]);
      enqueueSnackbar("Failed to fetch units", { variant: "error" });
    }
  };

  useEffect(() => {
    fetchUnits();
    handleUnitShow();
    fetchAttributes();
    fetchNodeAttributes();
    fetchAvailableOutputs();
  }, [stableRawMaterialInputs, stableNodeInputs, savedOutputIds, finalproduct]);

  useEffect(() => {
    outputs.forEach((output, index) => {
      if (output.isConnected === "yes" && output.connectedOutput && index > 0) fetchConnectedOutputAttributes(output.connectedOutput, index);
      else setConnectedOutputAttributes((prev) => ({ ...prev, [index]: { id: null, attributes: [] } }));
    });
  }, [outputs, availableOutputs]);

  useEffect(() => {
    if (!open) return;
    const initializeOutputs = async () => {
      let initialOutputs = [];
      let initialOutputIds = [];
      if (predefinedoutput && predefinedoutput.length > 0) {
        try {
          const fetchedOutputs = await Promise.all(
            predefinedoutput.map(async (id) => {
              const res = await fetcher([`/new/workflow-app/product-outputs/${id}/`]);
              if (!res || !res.id) return null;
              return { id: res.id, outputName: res.name || "", name: getRawFormulaString(res.formula) || "", unit: res.unit || "", description: res.description || "", quantity: res.value?.quantity || 0, isConnected: res.parent_output ? "yes" : "no", connectedOutput: "", inputForNextProcess: res.input_for_next_process ? "yes" : "no", question: res.question || "", linked_attributes: res.linked_attributes || [], measurement_unit: res.measurement_unit || null };
            })
          );
          initialOutputs = fetchedOutputs.filter(Boolean);
          initialOutputIds = initialOutputs.map((o) => o.id);
          if (initialOutputs.length > 0) setquestion(initialOutputs[0].question || "");
        } catch (err) {
          enqueueSnackbar("Could not load output data", { variant: "error" });
        }
      } else if (editingOutput) {
        if (Array.isArray(editingOutput)) {
          initialOutputs = editingOutput.map((output) => ({ id: output.id, outputName: output.name || "", name: getRawFormulaString(output.formula) || "", unit: output.unit || "", description: output.description || "", quantity: output.value?.quantity || 0, isConnected: output.parent_output ? "yes" : "no", connectedOutput: "", inputForNextProcess: output.input_for_next_process ? "yes" : "no", question: output.question || "", linked_attributes: output.linked_attributes || [], measurement_unit: output.measurement_unit || null }));
          initialOutputIds = editingOutput.map((o) => o.id);
          setquestion(editingOutput[0]?.question || "");
        } else {
          initialOutputs = [{ id: editingOutput.id, outputName: editingOutput.name || "", name: getRawFormulaString(editingOutput.formula) || "", unit: editingOutput.unit || "", description: editingOutput.description || "", quantity: editingOutput.value?.quantity || 0, isConnected: editingOutput.parent_output ? "yes" : "no", connectedOutput: "", inputForNextProcess: editingOutput.input_for_next_process ? "yes" : "no", question: editingOutput.question || "", linked_attributes: editingOutput.linked_attributes || [], measurement_unit: editingOutput.measurement_unit || null }];
          initialOutputIds = [editingOutput.id];
          setquestion(editingOutput.question || "");
        }
      }
      if (initialOutputs.length > 0) {
        setOutputs(initialOutputs);
        setNumberOfOutputs(initialOutputs.length);
        setAllOutputIds(initialOutputIds);
        setSavedOutputIds(initialOutputIds);
      } else {
        setOutputs([{ name: "", outputName: "", unit: "", description: "", quantity: 0, isConnected: "no", connectedOutput: "", inputForNextProcess: "no", question: "", measurement_unit: "" }]);
        setNumberOfOutputs(1);
        setAllOutputIds([]);
        setSavedOutputIds([]);
      }
    };
    initializeOutputs();
  }, [open, editingOutput, outputids]);

  const handleNumberOfOutputsChange = (value) => {
    try {
      let num = parseInt(value) || 1;
      num = Math.max(1, Math.min(10, num));
      setNumberOfOutputs(num);
      const newOutputs = [...outputs];
      if (num > newOutputs.length) {
        for (let i = newOutputs.length; i < num; i++) newOutputs.push({ name: "", outputName: "", unit: "", description: "", quantity: 0, isConnected: "no", connectedOutput: "", inputForNextProcess: "no", question: "", measurement_unit: "" });
      } else newOutputs.splice(num);
      setOutputs(newOutputs);
      setSelectedUnits((prev) => { const newUnits = [...(prev || [])]; if (num > newUnits.length) for (let i = newUnits.length; i < num; i++) newUnits.push(null); else newUnits.length = num; return newUnits; });
      if (getAllAvailableLinkedAttributes.length > 0) {
        const newLinkedAttributes = [];
        for (let i = 0; i < num; i++) {
          if (i < linked_attributes.length) newLinkedAttributes[i] = linked_attributes[i];
          else newLinkedAttributes[i] = getAllAvailableLinkedAttributes.map((attr) => ({ productId: attr.productId, attribute: attr.attribute }));
        }
        setLinkedAttributes(newLinkedAttributes);
      }
      setSelectedNodeOutput((prev) => { const newSelections = {}; for (let i = 0; i < num; i++) newSelections[i] = prev[i] || ""; return newSelections; });
    } catch (error) {
      enqueueSnackbar("Error updating number of outputs", { variant: "error" });
    }
  };

  const handleRefreshUnits = () => handleUnitShow();

  const handleOutputChange = (index, field, value) => {
    try {
      const newOutputs = [...outputs];
      if (field === "quantity") {
        const parsedValue = parseFloat(value);
        newOutputs[index][field] = Number.isNaN(parsedValue) || parsedValue < 0 ? 0 : parsedValue;
      } else newOutputs[index][field] = value;
      if (field === "isConnected" && value === "no") {
        newOutputs[index].connectedOutput = "";
        setConnectedOutputAttributes((prev) => ({ ...prev, [index]: { id: null, attributes: [] } }));
      }
      setOutputs(newOutputs);
    } catch (error) {
      enqueueSnackbar("Error updating output", { variant: "error" });
    }
  };

  const handleNodeOutputChange = (index, value) => {
    try {
      setSelectedNodeOutput((prev) => ({ ...prev, [index]: value }));
    } catch (error) {
      enqueueSnackbar("Error selecting node output", { variant: "error" });
    }
  };

  const handleNodeOutputChipClick = (index, outputName) => {
    try {
      const newOutputs = [...outputs];
      const currentFormula = newOutputs[index].name.trim();
      const selectedOutput = filteredNodeOutputs.flatMap((node) => node.outputs).find((o) => o.name === outputName);
      if (selectedOutput) {
        const outputRef = `o.${selectedOutput.id}`;
        newOutputs[index].name = currentFormula ? `${currentFormula} ${outputRef}` : outputRef;
        setOutputs(newOutputs);
      }
    } catch (error) {
      enqueueSnackbar("Error adding node output to formula", { variant: "error" });
    }
  };

  const handleAttributeClick = (outputIndex, materialName, attribute, type) => {
    try {
      const newOutputs = [...outputs];
      const currentFormula = newOutputs[outputIndex].name.trim();
      let attributeRef = "";
      if (type === "material" || type === "final_product") {
        const material = attributesList.find((m) => m.name === materialName);
        attributeRef = material ? `${material.id}.${attribute}` : `${materialName}.${attribute}`;
      } else if (type === "output") attributeRef = `${materialName}.${attribute}`;
      newOutputs[outputIndex].name = currentFormula ? `${currentFormula} ${attributeRef}` : attributeRef;
      setOutputs(newOutputs);
    } catch (error) {
      enqueueSnackbar("Error adding attribute to formula", { variant: "error" });
    }
  };

  const handleOperatorClick = (outputIndex, operator) => {
    try {
      const newOutputs = [...outputs];
      const currentFormula = newOutputs[outputIndex].name.trim();
      newOutputs[outputIndex].name = currentFormula ? `${currentFormula} ${operator} ` : `${operator} `;
      setOutputs(newOutputs);
    } catch (error) {
      enqueueSnackbar("Error adding operator to formula", { variant: "error" });
    }
  };

  const handleLinkedAttributesChange = (index, value) => {
    try {
      const newLinkedAttributes = [...linked_attributes];
      newLinkedAttributes[index] = value.map((val) => { const [productId, attribute] = val.split(":"); return { productId: Number(productId), attribute }; });
      setLinkedAttributes(newLinkedAttributes);
    } catch (error) {
      enqueueSnackbar("Error updating linked attributes", { variant: "error" });
    }
  };

  // New handlers for transformation section
  const handleTransformationChange = (index, value) => {
    setNeedsTransformation(prev => ({ ...prev, [index]: value }));
    
    // Reset transformation data when set to No
    if (value === 'no') {
      setSelectedTransformAttributes(prev => ({ ...prev, [index]: [] }));
      setTransformOperators(prev => ({ ...prev, [index]: {} }));
      setTransformUserValues(prev => ({ ...prev, [index]: {} }));
    }
  };

  const handleTransformAttributeToggle = (index, productId, attribute) => {
    setSelectedTransformAttributes(prev => {
      const current = prev[index] || [];
      const attributeKey = `${productId}:${attribute}`;
      const newSelection = current.includes(attributeKey)
        ? current.filter(item => item !== attributeKey)
        : [...current, attributeKey];
      
      // Update operators and values based on new selection count
      const updatedOperators = { ...(transformOperators[index] || {}) };
      const updatedValues = { ...(transformUserValues[index] || {}) };
      
      // Remove operator/value for attributes that were unselected
      newSelection.forEach(key => {
        if (!updatedOperators[key]) updatedOperators[key] = '+';
        if (updatedValues[key] === undefined) updatedValues[key] = '';
      });
      
      Object.keys(updatedOperators).forEach(key => {
        if (!newSelection.includes(key)) {
          delete updatedOperators[key];
          delete updatedValues[key];
        }
      });
      
      setTransformOperators(prev => ({ ...prev, [index]: updatedOperators }));
      setTransformUserValues(prev => ({ ...prev, [index]: updatedValues }));
      
      return { ...prev, [index]: newSelection };
    });
  };

  const handleTransformOperatorChange = (index, attributeKey, operator) => {
    setTransformOperators(prev => ({
      ...prev,
      [index]: {
        ...(prev[index] || {}),
        [attributeKey]: operator
      }
    }));
  };

  const handleTransformUserValueChange = (index, attributeKey, value) => {
    setTransformUserValues(prev => ({
      ...prev,
      [index]: {
        ...(prev[index] || {}),
        [attributeKey]: value
      }
    }));
  };

  const parseFormulaToPayload = (formulaStr, isConnected) => {
    try {
      formulaStr = formulaStr.replace(/\s+/g, " ").trim();
      if (!formulaStr) return { operation: "", operands: [], formulaStr, product: [], nodes: [], product_key: {} };
      const operatorMap = { "+": "add", "-": "subtract", "*": "multiply", "/": "divide" };
      const precedence = { "+": 1, "-": 1, "*": 2, "/": 2 };
      const tokens = formulaStr.match(/(\d+\.\w+|o\.\d+|\d*\.\d+|\d+|[+\-*\/()])/g) || [];
      if (tokens.length === 0) return { operation: "", operands: [], formulaStr, product: [], nodes: [], product_key: {} };
      const usedProductIds = new Set();
      const usedNodeIds = new Set();
      const parseExpression = (tokens, minPrecedence = 0) => {
        let left = parseAtom(tokens);
        while (tokens.length > 0 && tokens[0] in operatorMap && precedence[tokens[0]] >= minPrecedence) {
          const op = tokens.shift();
          left = { operation: operatorMap[op], operands: [left, parseExpression(tokens, precedence[op] + 1)] };
        }
        return left;
      };
      const parseAtom = (tokens) => {
        if (!tokens.length) throw new Error("Unexpected end of formula");
        if (tokens[0] === "(") { tokens.shift(); const expr = parseExpression(tokens); if (!tokens.length || tokens[0] !== ")") throw new Error("Unmatched parenthesis"); tokens.shift(); return expr; }
        const token = tokens.shift();
        if (token.match(/^\d*\.\d+$/)) return parseFloat(token);
        if (token.match(/^o\.\d+$/)) return token;
        if (token.match(/^\d+\.\w+$/)) {
          const lastDotIndex = token.lastIndexOf(".");
          const materialPart = token.substring(0, lastDotIndex);
          const materialId = parseFloat(materialPart);
          if (!Number.isNaN(materialId)) { usedProductIds.add(materialId); return token; }
          return token;
        }
        if (token.match(/^\d+$/)) return parseInt(token);
        throw new Error(`Unknown token: ${token}`);
      };
      const parsed = parseExpression([...tokens]);
      return { ...parsed, formulaStr, product: Array.from(usedProductIds), nodes: Array.from(usedNodeIds), product_key: {} };
    } catch (error) {
      enqueueSnackbar("Invalid formula. Please check syntax.", { variant: "error" });
      return { operation: "", operands: [], formulaStr, product: [], nodes: [], product_key: {} };
    }
  };

  const handleBackspace = (index, event) => {
    try {
      if (event.key !== "Backspace") return;
      const input = event.target;
      const cursorAtEnd = input.selectionStart === input.value.length && input.selectionEnd === input.value.length;
      if (cursorAtEnd) {
        const newOutputs = [...outputs];
        const currentFormula = newOutputs[index].name.trim();
        if (!currentFormula) return;
        const tokens = currentFormula.split(/\s+/).filter(Boolean);
        tokens.pop();
        newOutputs[index].name = tokens.join(" ");
        setOutputs(newOutputs);
        event.preventDefault();
      }
    } catch (error) {
      enqueueSnackbar("Error handling backspace", { variant: "error" });
    }
  };

  const handleAddUnitFromDialog = (newUnit) => {
    try {
      setAvailableUnits((prevUnits) => [...prevUnits, newUnit]);
      setShowAddUnitDialog(false);
    } catch (error) {
      enqueueSnackbar("Error adding new unit", { variant: "error" });
    }
  };

const handleSaveOutput = async (index) => {
  try {
    const output = outputs[index];
    if (!output.outputName.trim()) { 
      enqueueSnackbar(`Output Name is required for Output ${index + 1}`, { variant: "error" }); 
      return; 
    }
    if (output.isConnected === "yes" && !output.connectedOutput) { 
      enqueueSnackbar(`Please select a connected output or change to "No"`, { variant: "error" }); 
      return; 
    }
    
    const formula = parseFormulaToPayload(output.name, output.isConnected);
    
    // Get linked attributes for this specific output
    let currentLinkedAttributes = linked_attributes[index] || [];
    
    // If this output is connected to a previous node's output, we need to combine attributes
    if (output.isConnected === "yes" && output.connectedOutput) {
      // Find the connected output from availableOutputs
      const connectedOutput = availableOutputs.find(o => o.name === output.connectedOutput);
      
      if (connectedOutput) {
        // Fetch the complete output data to get its linked_attributes
        try {
          const connectedOutputData = await fetcher([`/new/workflow-app/product-outputs/${connectedOutput.index}/`]);
          
          if (connectedOutputData && connectedOutputData.linked_attributes) {
            // Combine the connected output's linked attributes with current ones
            // Using a Map to avoid duplicates based on productId + attribute
            const combinedAttributesMap = new Map();
            
            // Add connected output's linked attributes
            connectedOutputData.linked_attributes.forEach(attr => {
              const key = `${attr.productId}:${attr.attribute}`;
              combinedAttributesMap.set(key, attr);
            });
            
            // Add current output's linked attributes (these will override if same key exists)
            currentLinkedAttributes.forEach(attr => {
              const key = `${attr.productId}:${attr.attribute}`;
              combinedAttributesMap.set(key, attr);
            });
            
            // Convert back to array
            currentLinkedAttributes = Array.from(combinedAttributesMap.values());
            
            console.log('Combined linked attributes:', {
              fromConnected: connectedOutputData.linked_attributes,
              fromCurrent: linked_attributes[index],
              combined: currentLinkedAttributes
            });
          }
        } catch (error) {
          console.error('Error fetching connected output data:', error);
          enqueueSnackbar('Failed to fetch connected output attributes', { variant: 'warning' });
        }
      }
    }
    
    // If this is not the first output (index > 0), combine with previous outputs' linked attributes
    if (index > 0) {
      // Collect all linked attributes from previous outputs
      const previousLinkedAttributes = [];
      for (let i = 0; i < index; i++) {
        if (outputs[i]?.id) {
          try {
            // Fetch the complete output data for previous saved outputs
            const prevOutputData = await fetcher([`/new/workflow-app/product-outputs/${outputs[i].id}/`]);
            if (prevOutputData && prevOutputData.linked_attributes) {
              previousLinkedAttributes.push(...prevOutputData.linked_attributes);
            }
          } catch (error) {
            console.error(`Error fetching previous output ${i} data:`, error);
          }
        } else {
          // If not saved yet, use the current linked_attributes from state
          if (linked_attributes[i]) {
            previousLinkedAttributes.push(...linked_attributes[i]);
          }
        }
      }
      
      // Combine with current linked attributes
      if (previousLinkedAttributes.length > 0) {
        const combinedAttributesMap = new Map();
        
        // Add previous outputs' linked attributes
        previousLinkedAttributes.forEach(attr => {
          const key = `${attr.productId}:${attr.attribute}`;
          combinedAttributesMap.set(key, attr);
        });
        
        // Add current output's linked attributes (these will override if same key exists)
        currentLinkedAttributes.forEach(attr => {
          const key = `${attr.productId}:${attr.attribute}`;
          combinedAttributesMap.set(key, attr);
        });
        
        currentLinkedAttributes = Array.from(combinedAttributesMap.values());
        
        console.log('Combined with previous outputs:', {
          previous: previousLinkedAttributes,
          current: linked_attributes[index],
          combined: currentLinkedAttributes
        });
      }
    }
    
    const outputData = {
      name: output.outputName,
      value: { quantity: output.quantity },
      question: question || "",
      linked_attributes: currentLinkedAttributes, // Use the combined attributes
      unit: output.unit || "",
      description: output.description || "",
      input_for_next_process: output.inputForNextProcess === "yes",
      parent_output: output.isConnected === "yes" ? availableOutputs.find((o) => o.name === output.connectedOutput)?.index || null : null,
      product_variant: [],
      product_key: {},
      measurement_unit: output.measurement_unit,
    };
    
    if (formula?.operands?.length > 0) {
      outputData.formula = { 
        operation: formula.operation, 
        operands: formula.operands, 
        formulaStr: output.name.trim() 
      };
    } else {
      outputData.formula = null;
    }
    
    if (formula.product?.length > 0) outputData.product = formula.product;
    if (formula.nodes?.length > 0) outputData.nodes = formula.nodes;
    
    // Add transformation data if needed
    if (needsTransformation[index] === 'yes') {
      outputData.transformation = {
        attributes: selectedTransformAttributes[index] || [],
        operators: transformOperators[index] || {},
        userValues: transformUserValues[index] || {}
      };
    }
    
    let res;
    let isExistingOutput = false;
    let outputId = null;
    
    if (editingOutput && index === 0) { 
      outputId = editingOutput[0].id; 
      isExistingOutput = true; 
    } else if (output.id) { 
      outputId = output.id; 
      isExistingOutput = true; 
    }
    
    if (isExistingOutput && outputId && predefined === false) {
      res = await patchFetcher([`/new/workflow-app/product-outputs/${outputId}/`, outputData]);
      if (res && res.id) {
        const updatedOutputs = [...outputs];
        updatedOutputs[index].id = res.id;
        setOutputs(updatedOutputs);
        setAllOutputIds((prev) => prev.includes(res.id) ? prev : [...prev, res.id]);
        setSavedOutputIds((prev) => prev.includes(res.id) ? prev : [...prev, res.id]);
        await fetchAvailableOutputs();
        enqueueSnackbar(`Output "${output.outputName}" updated successfully!`, { variant: "success" });
      }
    } else {
      res = await postFetcher(["/new/workflow-app/product-outputs/", outputData]);
      if (res && res.id) {
        const updatedOutputs = [...outputs];
        updatedOutputs[index].id = res.id;
        setOutputs(updatedOutputs);
        setAllOutputIds((prev) => [...prev, res.id]);
        setSavedOutputIds((prev) => [...prev, res.id]);
        await fetchAvailableOutputs();
        enqueueSnackbar(`Output ${index + 1} created successfully`, { variant: "success" });
      }
    }
    
    if (res && res.id) {
      setLastPostedIndex(index);
      if (selectedNodeOutput[index]) {
        const selectedOutput = nodeOutputs.flatMap((node) => node.outputs).find((o) => o.name === selectedNodeOutput[index]);
        if (selectedOutput) onSave({ outputId: selectedOutput.id, outputName: selectedOutput.name, label: selectedOutput.name });
      }
      if (index === outputs.length - 1) {
        if (onUnitsSelected) onUnitsSelected(selectedUnits.filter((unit) => unit !== null));
        enqueueSnackbar("All changes saved!", { variant: "success" });
        handleClose();
      }
    } else {
      enqueueSnackbar("Failed to save output.", { variant: "error" });
    }
  } catch (error) {
    enqueueSnackbar(`Failed to save: ${error.message || "Unknown error"}`, { variant: "error" });
  }
};

  const handleClose = () => {
    try {
      setNumberOfOutputs(1);
      setOutputs([{ name: "", outputName: "", unit: "", description: "", quantity: 0, isConnected: "no", connectedOutput: "", inputForNextProcess: "no" }]);
      setLastPostedIndex(-1);
      setConnectedOutputAttributes({});
      setAvailableOutputs([]);
      setSelectedNodeOutput({});
      setNodeOutputs([]);
      setLinkedAttributes([]);
      setNeedsTransformation({});
      setSelectedTransformAttributes({});
      setTransformOperators({});
      setTransformUserValues({});
      onClose();
    } catch (error) {
      enqueueSnackbar("Error closing dialog", { variant: "error" });
    }
  };

  // ─── RENDER ────────────────────────────────────────────────────────────────────

  const renderOutputTab = (output, index) => {
    const isExistingOutput = output?.id !== undefined;
    const isDisabled = !isExistingOutput && index > lastPostedIndex + 1;
    const selectedOutputName = selectedNodeOutput[index] || "";
    const selectedOutput = filteredNodeOutputs.flatMap((node) => node.outputs).find((o) => o.name === selectedOutputName);
    const finalProduct = finalproduct && Array.isArray(finalproduct) && finalproduct.length > 0 ? finalproduct[0] : null;
    const finalProductAttributes = finalProduct ? attributesList.find((m) => m.id === finalProduct.id)?.attributes || [] : [];

    // Get all available material attributes for transformation
    const allMaterialAttributes = [];
    attributesList.forEach((material) => {
      material.attributes.forEach((attribute) => {
        allMaterialAttributes.push({
          productId: material.id,
          productName: material.name,
          attribute,
          displayName: `${material.name}: ${attribute}`
        });
      });
    });

    const SectionHeader = ({ icon, label }) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Box sx={{ width: 4, height: 16, borderRadius: 2, background: colors.accent }} />
        <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", color: colors.textSecondary, textTransform: "uppercase" }}>
          {label}
        </Typography>
      </Box>
    );

    return (
      <Box sx={{ p: 3, opacity: isDisabled ? 0.5 : 1, pointerEvents: isDisabled ? "none" : "auto", background: colors.bg }}>

        {/* Header row */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: "8px", background: colors.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography sx={{ color: "#fff", fontSize: "13px", fontWeight: 700 }}>{index + 1}</Typography>
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: colors.text }}>
              Output {index + 1}
              {output.id && (
                <Box component="span" sx={{ ml: 1, px: 1, py: 0.25, borderRadius: "4px", background: "#E8F5E9", color: "#2E7D32", fontSize: "11px", fontWeight: 600 }}>
                  Saved
                </Box>
              )}
            </Typography>
          </Box>
        </Box>

        {/* Attributes Section */}
        {nodeType !== "diamond" && (
          <Box sx={{ ...sectionStyle }}>
            <SectionHeader label="Available Attributes by Material" />
            {attributesList.length > 0 ? (
              attributesList.map((material) => (
                <Box key={material.name} sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: colors.textSecondary, mb: 0.75 }}>
                    {material.name} · Material
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {material.attributes.map((attribute) => (
                      <Chip
                        key={`${material.name}-${attribute}`}
                        label={attribute}
                        onClick={() => handleAttributeClick(index, material.name, attribute, "material")}
                        size="small"
                        sx={{ borderRadius: "6px", background: colors.chipBg, border: `1px solid ${colors.chipBorder}`, color: colors.text, fontSize: "12px", fontWeight: 500, cursor: "pointer", "&:hover": { background: colors.accent, color: "#fff", borderColor: colors.accent } }}
                      />
                    ))}
                  </Box>
                </Box>
              ))
            ) : (
              <Typography sx={{ fontSize: "13px", color: colors.textSecondary }}>No material attributes available</Typography>
            )}

            {finalProduct && (
              <Box sx={{ mt: 1.5 }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: colors.textSecondary, mb: 0.75 }}>
                  {finalProduct.name} · Final Product
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {finalProductAttributes.length > 0 ? (
                    finalProductAttributes.map((attribute) => (
                      <Chip
                        key={`${finalProduct.name}-${attribute}`}
                        label={attribute}
                        onClick={() => handleAttributeClick(index, finalProduct.name, attribute, "final_product")}
                        size="small"
                        sx={{ borderRadius: "6px", background: colors.chipBg, border: `1px solid ${colors.chipBorder}`, color: colors.text, fontSize: "12px", fontWeight: 500, cursor: "pointer", "&:hover": { background: colors.accent, color: "#fff", borderColor: colors.accent } }}
                      />
                    ))
                  ) : (
                    <Typography sx={{ fontSize: "13px", color: colors.textSecondary }}>No attributes available for final product</Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Previous Node Outputs */}
        <Box sx={{ ...sectionStyle }}>
          <SectionHeader label="Outputs from Previous Nodes" />
          {filteredNodeOutputs.length === 0 ? (
            <Typography sx={{ fontSize: "13px", color: colors.textSecondary }}>No available outputs from previous nodes</Typography>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: "13px" }}>Select Node Output</InputLabel>
              <Select
                value={selectedOutputName}
                onChange={(e) => handleNodeOutputChange(index, e.target.value)}
                label="Select Node Output"
                disabled={isDisabled || filteredNodeOutputs.length === 0}
                sx={{ borderRadius: "8px", fontSize: "13px", "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.border }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.borderDark } }}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {filteredNodeOutputs.flatMap((node) =>
                  node.outputs.map((output) => (
                    <MenuItem key={`${node.nodeId}-${output.id}`} value={output.name} sx={{ fontSize: "13px" }}>
                      {`${node.nodeName}: ${output.name}`}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          )}

          {selectedOutput && nodeType !== "diamond" && (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: colors.textSecondary, mb: 0.75 }}>Selected</Typography>
              <Chip
                label={`${selectedOutput.nodeName}: ${selectedOutput.name} (o.${selectedOutput.id})`}
                onClick={() => handleNodeOutputChipClick(index, selectedOutputName)}
                size="small"
                sx={{ borderRadius: "6px", background: colors.accent, color: "#fff", fontSize: "12px", fontWeight: 500, cursor: "pointer", border: "none" }}
              />
            </Box>
          )}
        </Box>

        {/* Operators */}
        {nodeType !== "diamond" && (
          <Box sx={{ ...sectionStyle }}>
            <SectionHeader label="Mathematical Operators" />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {["+", "-", "*", "/", "(", ")", "=", "0", "1", "2", "3", "4", "5", "6", "7", "8", "9"].map((operator) => (
                <Chip
                  key={operator}
                  label={operator}
                  onClick={() => handleOperatorClick(index, operator)}
                  size="small"
                  sx={{ borderRadius: "6px", background: colors.chipBg, border: `1px solid ${colors.chipBorder}`, color: colors.text, fontSize: "13px", fontWeight: 600, cursor: "pointer", minWidth: "36px", "&:hover": { background: colors.accent, color: "#fff", borderColor: colors.accent } }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Form Fields */}
        <Box sx={{ ...sectionStyle }}>
          <SectionHeader label="Output Details" />

          <TextField
            label="Output Name"
            value={output.outputName}
            onChange={(e) => handleOutputChange(index, "outputName", e.target.value)}
            fullWidth
            size="small"
            sx={fieldSx}
            placeholder="e.g., Cut Sheet"
            disabled={isDisabled}
          />

          {nodeType !== "diamond" && (
            <>
              <TextField
                label="Output Formula"
                value={output.name}
                onChange={(e) => handleOutputChange(index, "name", e.target.value)}
                onKeyDown={(e) => handleBackspace(index, e)}
                fullWidth
                size="small"
                sx={fieldSx}
                multiline
                rows={2}
                placeholder="Click attributes and operators above to build formula"
                disabled={isDisabled}
              />

              <FormControl fullWidth size="small" sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: colors.border }, "&:hover fieldset": { borderColor: colors.borderDark } } }}>
                <InputLabel sx={{ fontSize: "13px" }}>Linked Attributes</InputLabel>
                <Select
                  multiple
                  value={linked_attributes[index]?.map((attr) => `${attr.productId}:${attr.attribute}`) || []}
                  onChange={(e) => handleLinkedAttributesChange(index, e.target.value)}
                  label="Linked Attributes"
                  disabled={isDisabled || getAllAvailableLinkedAttributes.length === 0}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const [productId, attribute] = value.split(":");
                        const attrInfo = getAllAvailableLinkedAttributes.find((attr) => attr.productId === Number(productId) && attr.attribute === attribute);
                        return (
                          <Chip
                            key={value}
                            label={attrInfo?.displayName || `${productId}.${attribute}`}
                            size="small"
                            sx={{ borderRadius: "4px", background: colors.chipBg, fontSize: "11px" }}
                            onDelete={(e) => {
                              e.stopPropagation();
                              const currentValues = linked_attributes[index]?.map((attr) => `${attr.productId}:${attr.attribute}`) || [];
                              handleLinkedAttributesChange(index, currentValues.filter((v) => v !== value));
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  <MenuItem disabled sx={{ fontSize: "11px", fontWeight: 700, color: colors.textSecondary }}>— Raw Materials —</MenuItem>
                  {getAllAvailableLinkedAttributes.filter((attr) => attr.type === "material").map((attr) => (
                    <MenuItem key={`${attr.productId}-${attr.attribute}`} value={`${attr.productId}:${attr.attribute}`} sx={{ fontSize: "13px" }}>
                      <Checkbox checked={linked_attributes[index]?.some((la) => la.productId === attr.productId && la.attribute === attr.attribute) || false} size="small" />
                      <ListItemText primary={attr.displayName} primaryTypographyProps={{ fontSize: "13px" }} />
                    </MenuItem>
                  ))}
                  <MenuItem disabled sx={{ fontSize: "11px", fontWeight: 700, color: colors.textSecondary }}>— Node Outputs —</MenuItem>
                  {getAllAvailableLinkedAttributes.filter((attr) => attr.type === "node_output").map((attr) => (
                    <MenuItem key={`${attr.productId}-${attr.attribute}-${attr.outputName}`} value={`${attr.productId}:${attr.attribute}`} sx={{ fontSize: "13px" }}>
                      <Checkbox checked={linked_attributes[index]?.some((la) => la.productId === attr.productId && la.attribute === attr.attribute) || false} size="small" />
                      <ListItemText primary={attr.displayName} primaryTypographyProps={{ fontSize: "13px" }} />
                    </MenuItem>
                  ))}
                  {getAllAvailableLinkedAttributes.length === 0 && <MenuItem value="" disabled sx={{ fontSize: "13px" }}>No attributes available</MenuItem>}
                </Select>
              </FormControl>

              <TextField
                label="Description"
                value={output.description}
                onChange={(e) => handleOutputChange(index, "description", e.target.value)}
                fullWidth
                size="small"
                sx={fieldSx}
                multiline
                rows={2}
                placeholder="e.g., Smaller sheets cut from large sheets"
                disabled={isDisabled}
              />

              <FormControl fullWidth size="small" sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: colors.border }, "&:hover fieldset": { borderColor: colors.borderDark } } }}>
                <InputLabel sx={{ fontSize: "13px" }}>Measurement Unit</InputLabel>
                <Select
                  value={selectedUnits[index]?.id || ""}
                  onChange={(e) => {
                    const selectedUnitId = e.target.value;
                    const unitObj = availableMeasurementUnits.find((u) => u.id === selectedUnitId);
                    setSelectedUnits((prev) => { const updated = [...prev]; updated[index] = unitObj ? { id: unitObj.id, name: unitObj.name } : null; return updated; });
                    handleOutputChange(index, "measurement_unit", selectedUnitId);
                  }}
                  label="Measurement Unit"
                  disabled={isDisabled}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {availableMeasurementUnits.map((unit) => (
                    <MenuItem key={unit.id} value={unit.id} sx={{ fontSize: "13px" }}>{unit.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </Box>

        {/* New Transformation Section */}
        {nodeType !== "diamond" && allMaterialAttributes.length > 0 && (
          <Box sx={{ ...sectionStyle }}>
            <SectionHeader label="Transformation" />
            
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: colors.textSecondary, mb: 0.75 }}>
                Do you need transformation?
              </Typography>
              <RadioGroup 
                row 
                value={needsTransformation[index] || 'no'} 
                onChange={(e) => handleTransformationChange(index, e.target.value)}
              >
                <FormControlLabel 
                  value="yes" 
                  control={<Radio size="small" sx={{ color: colors.borderDark, "&.Mui-checked": { color: colors.accent } }} />} 
                  label={<Typography sx={{ fontSize: "13px" }}>Yes</Typography>} 
                />
                <FormControlLabel 
                  value="no" 
                  control={<Radio size="small" sx={{ color: colors.borderDark, "&.Mui-checked": { color: colors.accent } }} />} 
                  label={<Typography sx={{ fontSize: "13px" }}>No</Typography>} 
                />
              </RadioGroup>
            </Box>

            {needsTransformation[index] === 'yes' && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  {/* Left Column - Material Attributes */}
                  <Grid item xs={4}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        background: colors.bg, 
                        border: `1px solid ${colors.border}`,
                        borderRadius: "8px",
                        height: '100%',
                        maxHeight: '300px',
                        overflow: 'auto'
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", fontWeight: 600, color: colors.textSecondary, mb: 1 }}>
                        Select Material Attributes
                      </Typography>
                      <FormControl component="fieldset" fullWidth>
                        <FormGroup>
                          {allMaterialAttributes.map((attr) => {
                            const attributeKey = `${attr.productId}:${attr.attribute}`;
                            return (
                              <FormControlLabel
                                key={attributeKey}
                                control={
                                  <Checkbox
                                    checked={selectedTransformAttributes[index]?.includes(attributeKey) || false}
                                    onChange={() => handleTransformAttributeToggle(index, attr.productId, attr.attribute)}
                                    size="small"
                                    sx={{ 
                                      color: colors.borderDark,
                                      '&.Mui-checked': { color: colors.accent }
                                    }}
                                  />
                                }
                                label={
                                  <Typography sx={{ fontSize: "12px" }}>
                                    {attr.displayName}
                                  </Typography>
                                }
                              />
                            );
                          })}
                        </FormGroup>
                      </FormControl>
                    </Paper>
                  </Grid>

                  {/* Center Column - Operators */}
                  <Grid item xs={4}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        background: colors.bg, 
                        border: `1px solid ${colors.border}`,
                        borderRadius: "8px",
                        height: '100%',
                        maxHeight: '300px',
                        overflow: 'auto'
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", fontWeight: 600, color: colors.textSecondary, mb: 1 }}>
                        Select Operators
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {(selectedTransformAttributes[index] || []).map((attributeKey) => {
                          const [productId, attribute] = attributeKey.split(':');
                          const attrInfo = allMaterialAttributes.find(
                            a => a.productId === Number(productId) && a.attribute === attribute
                          );
                          return (
                            <Box key={attributeKey} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography sx={{ fontSize: "11px", color: colors.textSecondary }}>
                                {attrInfo?.displayName || attributeKey}
                              </Typography>
                              <FormControl fullWidth size="small">
                                <Select
                                  value={transformOperators[index]?.[attributeKey] || '+'}
                                  onChange={(e) => handleTransformOperatorChange(index, attributeKey, e.target.value)}
                                  sx={{
                                    fontSize: "12px",
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border }
                                  }}
                                >
                                  <MenuItem value="+">Add (+)</MenuItem>
                                  <MenuItem value="-">Subtract (-)</MenuItem>
                                  <MenuItem value="*">Multiply (×)</MenuItem>
                                  <MenuItem value="/">Divide (÷)</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                          );
                        })}
                        {(selectedTransformAttributes[index] || []).length === 0 && (
                          <Typography sx={{ fontSize: "12px", color: colors.textSecondary, textAlign: 'center', py: 2 }}>
                            Select attributes on the left
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Right Column - User Values */}
                  <Grid item xs={4}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        background: colors.bg, 
                        border: `1px solid ${colors.border}`,
                        borderRadius: "8px",
                        height: '100%',
                        maxHeight: '300px',
                        overflow: 'auto'
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", fontWeight: 600, color: colors.textSecondary, mb: 1 }}>
                        Enter Values
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {(selectedTransformAttributes[index] || []).map((attributeKey) => {
                          const [productId, attribute] = attributeKey.split(':');
                          const attrInfo = allMaterialAttributes.find(
                            a => a.productId === Number(productId) && a.attribute === attribute
                          );
                          return (
                            <Box key={attributeKey} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography sx={{ fontSize: "11px", color: colors.textSecondary }}>
                                {attrInfo?.displayName || attributeKey}
                              </Typography>
                              <TextField
                                size="small"
                                type="number"
                                value={transformUserValues[index]?.[attributeKey] || ''}
                                onChange={(e) => handleTransformUserValueChange(index, attributeKey, e.target.value)}
                                placeholder="Enter value"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    '& fieldset': { borderColor: colors.border }
                                  }
                                }}
                              />
                            </Box>
                          );
                        })}
                        {(selectedTransformAttributes[index] || []).length === 0 && (
                          <Typography sx={{ fontSize: "12px", color: colors.textSecondary, textAlign: 'center', py: 2 }}>
                            Select attributes on the left
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}

        {/* Radio groups */}
        {nodeType !== "diamond" && (
          <Box sx={{ ...sectionStyle }}>
            <SectionHeader label="Connection Settings" />

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: colors.textSecondary, mb: 0.75 }}>
                Is this output connected to any other output?
              </Typography>
              <RadioGroup row value={output.isConnected} onChange={(e) => handleOutputChange(index, "isConnected", e.target.value)}>
                <FormControlLabel value="yes" control={<Radio size="small" sx={{ color: colors.borderDark, "&.Mui-checked": { color: colors.accent } }} />} label={<Typography sx={{ fontSize: "13px" }}>Yes</Typography>} />
                <FormControlLabel value="no" control={<Radio size="small" sx={{ color: colors.borderDark, "&.Mui-checked": { color: colors.accent } }} />} label={<Typography sx={{ fontSize: "13px" }}>No</Typography>} />
              </RadioGroup>
            </Box>

            {output.isConnected === "yes" && (
              <FormControl fullWidth size="small" sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: colors.border } } }}>
                <InputLabel sx={{ fontSize: "13px" }}>Connected Output</InputLabel>
                <Select value={output.connectedOutput} onChange={(e) => handleOutputChange(index, "connectedOutput", e.target.value)} label="Connected Output" disabled={isDisabled}>
                  {availableOutputs.map((out) => (
                    <MenuItem key={out.name} value={out.name} sx={{ fontSize: "13px" }}>{out.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: colors.textSecondary, mb: 0.75 }}>
                Is this output an input for the next process?
              </Typography>
              <RadioGroup row value={output.inputForNextProcess} onChange={(e) => handleOutputChange(index, "inputForNextProcess", e.target.value)}>
                <FormControlLabel value="yes" control={<Radio size="small" sx={{ color: colors.borderDark, "&.Mui-checked": { color: colors.accent } }} />} label={<Typography sx={{ fontSize: "13px" }}>Yes</Typography>} />
                <FormControlLabel value="no" control={<Radio size="small" sx={{ color: colors.borderDark, "&.Mui-checked": { color: colors.accent } }} />} label={<Typography sx={{ fontSize: "13px" }}>No</Typography>} />
              </RadioGroup>
            </Box>
          </Box>
        )}

        {/* Action buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1.5 }}>
          {output.id && (
            <Button
              variant="outlined"
              onClick={() => handleDeleteOutput(index)}
              disabled={isDisabled}
              sx={{ borderRadius: "8px", borderColor: "#FECDD2", color: colors.error, fontSize: "13px", fontWeight: 600, "&:hover": { background: "#FEF2F2", borderColor: colors.error } }}
            >
              Delete Output {index + 1}
            </Button>
          )}
          <Button
            onClick={() => handleSaveOutput(index)}
            variant="contained"
            disabled={isDisabled || !output.outputName.trim()}
            sx={{ borderRadius: "8px", background: colors.accent, color: "#fff", fontSize: "13px", fontWeight: 700, boxShadow: "none", px: 3, "&:hover": { background: colors.accentHover, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }, "&:disabled": { background: colors.border, color: colors.textSecondary } }}
          >
            Save Output {index + 1}
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "14px",
            background: colors.bg,
            boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
            overflow: "hidden",
          },
        }}
      >
        {/* Dialog Title */}
        <DialogTitle
          sx={{
            background: colors.surface,
            borderBottom: `1px solid ${colors.border}`,
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: colors.accent }} />
          <Typography sx={{ fontWeight: 700, fontSize: "16px", color: colors.text }}>
            Add New Output
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, background: colors.bg, borderColor: colors.border }}>
          {/* Top controls */}
          <Box sx={{ px: 3, pt: 2.5, pb: 0, background: colors.surface, borderBottom: `1px solid ${colors.border}` }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
              <TextField
                label="Number of Outputs"
                type="number"
                value={numberOfOutputs}
                onChange={(e) => handleNumberOfOutputsChange(e.target.value)}
                inputProps={{ min: 1, max: 10 }}
                size="small"
                sx={{ width: 180, ...fieldSx, mb: 0 }}
              />

              {nodeType === "diamond" && (
                <TextField
                  label="Question For Decisions"
                  value={question || ""}
                  onChange={(e) => setquestion(e.target.value)}
                  size="small"
                  sx={{ flex: 1, ...fieldSx, mb: 0 }}
                  multiline
                  rows={1}
                  placeholder="Ask the question"
                />
              )}
            </Box>

            {/* Tabs */}
            <Tabs
              value={currentTab}
              onChange={(_, v) => setCurrentTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTab-root": {
                  fontSize: "13px",
                  fontWeight: 600,
                  color: colors.textSecondary,
                  textTransform: "none",
                  minHeight: "40px",
                  borderRadius: "8px 8px 0 0",
                  "&.Mui-selected": { color: colors.tabSelected },
                },
                "& .MuiTabs-indicator": { background: colors.tabBorder, height: "2px" },
              }}
            >
              {outputs.map((_, index) => (
                <Tab
                  key={index}
                  label={`Output ${index + 1}${outputs[index]?.id ? " ✓" : ""}`}
                  disabled={!outputs[index]?.id && index > lastPostedIndex + 1}
                />
              ))}
            </Tabs>
          </Box>

          {/* Tab content */}
          {outputs.map((output, index) => (
            <div key={index} style={{ display: currentTab === index ? "block" : "none" }}>
              {renderOutputTab(output, index)}
            </div>
          ))}
        </DialogContent>

        <DialogActions
          sx={{
            background: colors.surface,
            borderTop: `1px solid ${colors.border}`,
            px: 3,
            py: 2,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ borderRadius: "8px", borderColor: colors.border, color: colors.textSecondary, fontWeight: 600, fontSize: "13px", "&:hover": { borderColor: colors.borderDark, background: colors.bg } }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <OutputDeleteConfirmationDialog
        key={`delete-dialog-${directLinks.length}-${directNodes.length}-${downstreamLinks.length}-${downstreamNodes.length}`}
        open={deleteConfirmOpen}
        onClose={handleClosedelete}
        onConfirm={confirmDelete}
        directLinks={directLinks}
        downstreamLinks={downstreamLinks}
        directNodes={directNodes}
        downstreamNodes={downstreamNodes}
        selectedLinks={selectedLinks}
        setSelectedLinks={setSelectedLinks}
        selectedNodes={selectedNodes}
        setSelectedNodes={setSelectedNodes}
        setdeleteeditDialog={setdeleteeditDialog}
      />

      <AddUnitDialog
        open={showAddUnitDialog}
        onClose={() => setShowAddUnitDialog(false)}
        onRefresh={handleRefreshUnits}
        onSave={handleAddUnitFromDialog}
        existingUnits={availableUnits}
      />

      <DeleteConfirmationInsideEdit
        open={showdeleteeditDialog}
        onClose={() => setdeleteeditDialog(false)}
        permanentdeleteeditnode={permanentdeleteeditnode}
        setpermanentdeleteeditnode={setpermanentdeleteeditnode}
        onSave={() => confirmDelete()}
      />
    </>
  );
};

export default AddOutputDialog;